import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"
import { ArrowLeft, Save } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App';
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService';
import { RechazoModal } from "./RechazoModal";

interface FormData {
  fecha: string
  hora: string
  grados: string
  hr: string
  columnas: string
  mallas: string
  no1_columnas: string
  no1_mallas: string
  no2_columnas: string
  no2_mallas: string
  no3_columnas: string
  no3_mallas: string
  no4_columnas: string
  no4_mallas: string
  no5_columnas: string
  no5_mallas: string
  promedioColumnas: string
  promedioMallas: string
  resultadoColumnas: string
  resultadoMallas: string
  realizo: string
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean
  numeroSolicitud?: string
  idTermohigrometro?: string
  desviacion_metodo?: string;
  tamano_modelo?: string;
  tamano_modelo_repetido?: string;
  total_metros?: string;
}

interface NMXA134FormProps {
  pedidoNumero: string
  pedidoRowId?: string
  onBack: () => void
  onSave: (data: FormData) => void
  realizoUser?: string
  initialData?: Partial<FormData>
  mode?: 'create' | 'view'
  rol?: string // <-- NUEVO: rol del usuario
  onRefreshBackend?: () => void // <-- NUEVO: función para refrescar el backend
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function NMXA134Form({ pedidoNumero, pedidoRowId, onBack, onSave, realizoUser, initialData = {}, mode = 'create', rol, onRefreshBackend, onAutosave }: NMXA134FormProps) {
  // Soporta array y objeto para initialData
  const initialDataObj = Array.isArray(initialData) ? initialData[0] : initialData || {};

  const [formData, setFormData] = useState<any>({
    fecha: "",
    hora: "",
    grados: "",
    hr: "",
    columnas: "",
    mallas: "",
    no1_columnas: "",
    no2_columnas: "",
    no3_columnas: "",
    no4_columnas: "",
    no5_columnas: "",
    no1_mallas: "",
    no2_mallas: "",
    no3_mallas: "",
    no4_mallas: "",
    no5_mallas: "",
    promedioColumnas: "",
    promedioMallas: "",
    resultadoColumnas: "",
    resultadoMallas: "",
    realizo: "",
    superviso: "",
    observaciones: "",
    desviacion_metodo: initialDataObj?.desviacion_metodo || "",
    tamano_modelo: initialDataObj?.tamano_modelo || "",
    tamano_modelo_repetido: initialDataObj?.tamano_modelo_repetido || "",
    total_metros: initialDataObj?.total_metros || "",
    ...initialDataObj,
    PRUEBA: (initialDataObj as any)?.PRUEBA || '',
    ROW_ID: (initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || '',
    mostrarObservaciones: (initialDataObj as any)?.MOSTRAR_OBSERVACIONES === true,
  });

  const [editMode, setEditMode] = useState(false)

  // Estados para el modal de rechazo
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  useEffect(() => {
    if (mode === 'create') {
      setFormData((prev: any) => ({ ...prev, realizo: realizoUser || "" }))
    }
  }, [realizoUser, mode])

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    setFormData((prev: any) => ({ ...prev, ...initialData }))
    setEstatus((initialData as any)?.ACEPTADO || '')
    setEditMode(false)
  }, [initialData])

  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol;

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  // Calcular promedios y resultados automáticamente
  useEffect(() => {
    const valoresColumnas = [
      parseFloat(formData.no1_columnas) || 0,
      parseFloat(formData.no2_columnas) || 0,
      parseFloat(formData.no3_columnas) || 0,
      parseFloat(formData.no4_columnas) || 0,
      parseFloat(formData.no5_columnas) || 0
    ].filter(val => val > 0)

    const valoresMallas = [
      parseFloat(formData.no1_mallas) || 0,
      parseFloat(formData.no2_mallas) || 0,
      parseFloat(formData.no3_mallas) || 0,
      parseFloat(formData.no4_mallas) || 0,
      parseFloat(formData.no5_mallas) || 0
    ].filter(val => val > 0)

    // Calcular promedios
    const promedioColumnas = valoresColumnas.length > 0 
      ? (valoresColumnas.reduce((sum, val) => sum + val, 0) / valoresColumnas.length)
      : ""

    const promedioMallas = valoresMallas.length > 0 
      ? (valoresMallas.reduce((sum, val) => sum + val, 0) / valoresMallas.length)
      : ""

    // Calcular resultados como promedio/valor
    let resultadoColumnas = ""
    let resultadoMallas = ""
    let redondeoColumnas = ""
    let redondeoMallas = ""
    if (promedioColumnas && parseFloat(formData.columnas) > 0) {
      resultadoColumnas = (promedioColumnas / parseFloat(formData.columnas)).toFixed(2)
      redondeoColumnas = Math.round(Number(resultadoColumnas)).toString()
    }
    if (promedioMallas && parseFloat(formData.mallas) > 0) {
      resultadoMallas = (promedioMallas / parseFloat(formData.mallas)).toFixed(2)
      redondeoMallas = Math.round(Number(resultadoMallas)).toString()
    }

    setFormData((prev: any) => ({
      ...prev,
      promedioColumnas: promedioColumnas ? promedioColumnas.toFixed(1) : "",
      promedioMallas: promedioMallas ? promedioMallas.toFixed(1) : "",
      resultadoColumnas,
      resultadoMallas,
      redondeoColumnas,
      redondeoMallas
    }))
  }, [
    formData.no1_columnas, formData.no1_mallas,
    formData.no2_columnas, formData.no2_mallas,
    formData.no3_columnas, formData.no3_mallas,
    formData.no4_columnas, formData.no4_mallas,
    formData.no5_columnas, formData.no5_mallas,
    formData.columnas,
    formData.mallas
  ])

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      onSave(formData)
    } finally {
      setGuardando(false)
    }
  }

  // --- ADMIN ACTIONS ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>(
    (initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || formData.ROW_ID || ''
  )
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialDataObj as any)?.nomId || '')
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoValue = rowId; // <-- PEDIDO ahora es el ROW_ID, no el número de pedido
  const pruebaValue = (formData as any).PRUEBA || '';

  // Estado local robusto para estatus (aceptado/rechazado)
  const getInitialEstatus = () => {
    const raw = (initialDataObj as any)?.ACEPTADO ?? (initialDataObj as any)?.ACEPTAR ?? '';
    if (typeof raw === 'string') {
      const val = raw.toLowerCase().trim();
      if (val === 'aceptado' || val === 'rechazado') return val;
    }
    return '';
  };
  const [estatus, setEstatus] = useState<string>(getInitialEstatus());

  // Sincroniza estatus local si cambia initialData
  useEffect(() => {
    setEstatus(getInitialEstatus());
  }, [initialDataObj?.ACEPTADO, initialDataObj?.ACEPTAR]);

  // DEBUG: Log para revisar si ACEPTADO llega desde PedidosView
  console.log('[NMX-A-134-Form] initialDataObj?.ACEPTADO:', initialDataObj?.ACEPTADO, '| formData.ACEPTADO:', formData.ACEPTADO, '| estatus local:', estatus);

  const isAceptado = estatus === 'aceptado' || estatus === 'rechazado';

  const formPayload = () => {
    const { ROW_ID, PRUEBA, PEDIDO, ...rest } = formData as any
    return rest
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-134-INNTEX-2013 DENSIDAD DE TEJIDO DE PUNTO',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado') => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-134?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-134?';
    if (!window.confirm(confirmMsg)) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: rowId, // <-- ahora PEDIDO manda el ROW_ID
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      desviacion_metodo: formData.desviacion_metodo,
      tamano_modelo: formData.tamano_modelo,
      tamano_modelo_repetido: formData.tamano_modelo_repetido,
      total_metros: formData.total_metros,
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, undefined, (payload as any).superviso, nomId);
      setEditMode(false);
      setEstatus(payload.ACEPTAR); // Actualiza estatus local
      setFormData((prev: any) => ({ ...prev, ACEPTADO: accion, ACEPTAR: payload.ACEPTAR })); // Actualiza formData local
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 150); // Espera para refrescar
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    }
  };

  const handleRechazar = () => setRechazoModalOpen(true);

  const handleRechazoSubmit = async (razon: string) => {
    setRechazoLoading(true);
    const confirmMsg = 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba NMX-A-134?';
    if (!window.confirm(confirmMsg)) {
      setRechazoLoading(false);
      setRechazoModalOpen(false);
      return;
    }
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: rowId, // <-- ahora PEDIDO manda el ROW_ID
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'rechazado',
      ACEPTADO: 'rechazado',
      RAZON_RECHAZO: razon,
      desviacion_metodo: formData.desviacion_metodo,
      tamano_modelo: formData.tamano_modelo,
      tamano_modelo_repetido: formData.tamano_modelo_repetido,
      total_metros: formData.total_metros,
    };
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId);
      setEditMode(false);
      setEstatus('rechazado');
      setFormData((prev: any) => ({ ...prev, ACEPTADO: 'rechazado', ACEPTAR: 'rechazado' }));
      setRechazoModalOpen(false);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 150);
      toast.success('Prueba rechazada correctamente.');
    } catch (err: any) {
      alert('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setRechazoLoading(false);
    }
  };

  const handleEditar = () => setEditMode(true);

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-134? Esta acción no se puede deshacer.')) return;
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: rowId, // <-- ahora PEDIDO manda el ROW_ID
      PRUEBA: pruebaValue,
      superviso: realizoUser || formData.superviso || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado',
      desviacion_metodo: formData.desviacion_metodo,
      tamano_modelo: formData.tamano_modelo,
      tamano_modelo_repetido: formData.tamano_modelo_repetido,
      total_metros: formData.total_metros,
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  };

  // Determina el rol efectivo (igual que otros forms)
  const isReadOnly = mode === 'view';

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Modal de rechazo */}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        onSubmit={handleRechazoSubmit}
        loading={rechazoLoading}
      />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            NMX-A-134-INNTEX-2013 
          </h1>
          <p className="text-muted-foreground">
            DETERMINACIÓN DE LA DENSIDAD DEL TEJIDO DE PUNTO
          </p>
          <p className="text-sm text-primary font-medium mt-1">Método: MET-7.2-6</p>
          <p className="text-muted-foreground">Pedido: {pedidoNumero}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Condiciones de Ensayo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <Label htmlFor="fecha">FECHA</Label>
              <Input
                id="fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) => handleInputChange('fecha', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="hora">HORA</Label>
              <Input
                id="hora"
                type="time"
                value={formData.hora}
                onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="grados">°C</Label>
              <Input
                id="grados"
                type="number"
                step="0.1"
                placeholder="20.0"
                value={formData.grados}
                onChange={(e) => handleInputChange('grados', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="hr">% HR</Label>
              <Input
                id="hr"
                type="number"
                step="0.1"
                placeholder="65.0"
                value={formData.hr}
                onChange={(e) => handleInputChange('hr', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="numeroSolicitud">NÚMERO DE SOLICITUD</Label>
              <Input
                id="numeroSolicitud"
                type="text"
                placeholder="Ej. 12345"
                value={formData.numeroSolicitud || ""}
                onChange={(e) => handleInputChange('numeroSolicitud', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="idTermohigrometro">ID DE TERMOHIGRÓMETRO</Label>
              <select
                id="idTermohigrometro"
                value={formData.idTermohigrometro || ""}
                onChange={e => handleInputChange('idTermohigrometro', e.target.value)}
                disabled={mode === 'view' && !editMode}
                className="w-full rounded-md border border-input bg-muted-foreground/10 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Selecciona</option>
                <option value="E-59">E-59</option>
                <option value="E-57">E-57</option>
                <option value="E-56">E-56</option>
              </select>
            </div>
            <div>
              <Label htmlFor="desviacion_metodo">DESVIACIÓN AL MÉTODO</Label>
              <Input
                id="desviacion_metodo"
                type="text"
                placeholder="Describa desviación al método"
                value={formData.desviacion_metodo || ""}
                onChange={e => handleInputChange('desviacion_metodo', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="tamano_modelo">TAMAÑO DE CADA DISEÑO COMPONENTE DEL MODELO</Label>
              <Input
                id="tamano_modelo"
                type="text"
                placeholder="Ej. 10x20 cm"
                value={formData.tamano_modelo || ""}
                onChange={e => handleInputChange('tamano_modelo', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="tamano_modelo_repetido">TAMAÑO DEL MODELO REPETIDO</Label>
              <Input
                id="tamano_modelo_repetido"
                type="text"
                placeholder="Ej. 30x40 cm"
                value={formData.tamano_modelo_repetido || ""}
                onChange={e => handleInputChange('tamano_modelo_repetido', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
            <div>
              <Label htmlFor="total_metros">TOTAL DE METROS QUE CONSTITUYE A LOS TEJIDOS</Label>
              <Input
                id="total_metros"
                type="text"
                placeholder="Ej. 100"
                value={formData.total_metros || ""}
                onChange={e => handleInputChange('total_metros', e.target.value)}
                readOnly={mode === 'view' && !editMode}
                disabled={mode === 'view' && !editMode}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="columnas">COLUMNAS</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="columnas"
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  value={formData.columnas}
                  onChange={(e) => handleInputChange('columnas', e.target.value)}
                  readOnly={mode === 'view' && !editMode}
                  disabled={mode === 'view' && !editMode}
                />
                <span className="text-muted-foreground">cm</span>
              </div>
            </div>
            <div>
              <Label htmlFor="mallas">MALLAS</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="mallas"
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  value={formData.mallas}
                  onChange={(e) => handleInputChange('mallas', e.target.value)}
                  readOnly={mode === 'view' && !editMode}
                  disabled={mode === 'view' && !editMode}
                />
                <span className="text-muted-foreground">cm</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tabla de mediciones */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Mediciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border p-3 text-center">No.</th>
                      <th className="border border-border p-3 text-center">COLUMNAS</th>
                      <th className="border border-border p-3 text-center">MALLAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <tr key={num}>
                        <td className="border border-border p-3 text-center">{num}</td>
                        <td className="border border-border p-2">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="0.0"
                            value={formData[`no${num}_columnas` as keyof FormData]}
                            onChange={(e) => handleInputChange(`no${num}_columnas` as keyof FormData, e.target.value)}
                            className="text-center"
                            readOnly={mode === 'view' && !editMode}
                            disabled={mode === 'view' && !editMode}
                          />
                        </td>
                        <td className="border border-border p-2">
                          <Input
                            type="number"
                            step="0.1"
                            placeholder="0.0"
                            value={formData[`no${num}_mallas` as keyof FormData]}
                            onChange={(e) => handleInputChange(`no${num}_mallas` as keyof FormData, e.target.value)}
                            className="text-center"
                            readOnly={mode === 'view' && !editMode}
                            disabled={mode === 'view' && !editMode}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-accent">
                      <td className="border border-border p-3 text-center">PROMEDIO</td>
                      <td className="border border-border p-3 text-center">
                        <span className="font-medium">{formData.promedioColumnas}</span>
                      </td>
                      <td className="border border-border p-3 text-center">
                        <span className="font-medium">{formData.promedioMallas}</span>
                      </td>
                    </tr>
                    <tr className="bg-primary/10">
                      <td className="border border-border p-3 text-center">RESULTADO</td>
                      <td className="border border-border p-3 text-center">
                        <span className="font-medium">
                          {formData.resultadoColumnas && `${formData.resultadoColumnas} `}
                        </span>
                      </td>
                      <td className="border border-border p-3 text-center">
                        <span className="font-medium">
                          {formData.resultadoMallas && `${formData.resultadoMallas} `}
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-primary/20">
                      <td className="border border-border p-3 text-center">REDONDEO</td>
                      <td className="border border-border p-3 text-center">
                        <span className="font-medium">
                          {formData.resultadoColumnas ? Math.round(Number(formData.resultadoColumnas)) : ''}
                        </span>
                      </td>
                      <td className="border border-border p-3 text-center">
                        <span className="font-medium">
                          {formData.resultadoMallas ? Math.round(Number(formData.resultadoMallas)) : ''}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Personal */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Personal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="realizo">REALIZÓ</Label>
                <Input
                  id="realizo"
                  placeholder="Nombre del técnico"
                  value={formData.realizo}
                  readOnly
                  className="bg-muted-foreground/10 cursor-not-allowed"
                />
              </div>
              <div>
                <Label htmlFor="superviso">SUPERVISÓ</Label>
                <Input
                  id="superviso"
                  placeholder="Nombre del supervisor"
                  value={formData.superviso}
                  onChange={(e) => handleInputChange('superviso', e.target.value)}
                  disabled={true}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Observaciones</CardTitle>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            Mostrar en Reporte Cliente
            <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
              onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
          </span>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="observaciones">OBSERVACIONES</Label>
            <Textarea
              id="observaciones"
              placeholder="Escriba sus observaciones aquí..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              rows={4}
              readOnly={mode === 'view' && !editMode}
              disabled={mode === 'view' && !editMode}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush}>
          Cancelar
        </Button>
        {/* Botones de acciones en modo view y no aceptado SOLO PARA ADMIN */}
        {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
          <>
            <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
            <Button variant="destructive" onClick={handleRechazar}>Rechazar</Button>
            <Button variant="secondary" onClick={handleEditar}>Editar</Button>
          </>
        )}
        {/* Botón guardar edición SOLO PARA ADMIN */}
        {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
          <Button variant="default" onClick={handleGuardarEdicion}>
            Guardar Edición
          </Button>
        )}
        {/* Botón guardar en modo create */}
        {mode === 'create' && (
          <Button onClick={handleSave} disabled={guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar Formulario'}
          </Button>
        )}
      </div>
    </div>
  )
}